import React from 'react';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Color } from 'core';
import styled from 'styled-components';

// Form Layout Components
export const ModalContent = styled.div`
  min-height: 400px;
  display: flex;
  flex-direction: column;
`;

export const FormContainer = styled.div`
  flex: 1;
`;

export const FormGroup = styled.div`
  margin-bottom: 24px;
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
`;

// Label Component
interface LabelProps {
  children: React.ReactNode;
  required?: boolean;
}

export const FormLabel = ({ children, required = false }: LabelProps) => (
  <Label>
    {children}
    {required && <RequiredAsterisk> *</RequiredAsterisk>}
  </Label>
);

const Label = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 500;
  color: ${Color.primaryBlack};
  margin-bottom: 8px;
`;

const RequiredAsterisk = styled.span`
  color: #dc3545;
`;

// Input Components
interface InputProps {
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}

export const FormInput = ({ type = 'text', value, onChange, placeholder }: InputProps) => (
  <Input type={type} value={value} onChange={onChange} placeholder={placeholder} />
);

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    border-color: ${Color.primaryBlue};
  }

  &::placeholder {
    color: #999;
  }
`;

// TextArea Component
interface TextAreaProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  rows?: number;
}

export const FormTextArea = ({ value, onChange, placeholder, rows = 6 }: TextAreaProps) => (
  <TextArea value={value} onChange={onChange} placeholder={placeholder} rows={rows} />
);

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  outline: none;
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    border-color: ${Color.primaryBlue};
  }

  &::placeholder {
    color: #999;
  }
`;

// Select Component
interface SelectProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: string[];
}

export const FormSelect = ({ value, onChange, options }: SelectProps) => (
  <SelectWrapper>
    <Select value={value} onChange={onChange}>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </Select>
    <SelectIcon>
      <FontAwesomeIcon icon={faChevronDown} />
    </SelectIcon>
  </SelectWrapper>
);

const SelectWrapper = styled.div`
  position: relative;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  padding-right: 40px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  appearance: none;
  background: white;
  cursor: pointer;

  &:focus {
    border-color: ${Color.primaryBlue};
  }
`;

const SelectIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${Color.darkGray};
  pointer-events: none;
`;

// Primary Button Component
interface PrimaryButtonProps {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}

export const FormPrimaryButton = ({ onClick, disabled = false, children }: PrimaryButtonProps) => (
  <PrimaryButton onClick={onClick} disabled={disabled}>
    {children}
  </PrimaryButton>
);

const PrimaryButton = styled.button`
  background: ${Color.primaryBlue};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #4a5fc7;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;
